// Edits of the default template (pure; template format: { id, code, kind: "discipline"|"system",
// name_ka, name_en, sheet_code, category, location_rule, in_preset, children }). Same rule as the
// editor: a discipline holds either sub-disciplines or systems, a system holds nothing, the top
// level holds disciplines only. A refused edit throws TemplateEditError.

export class TemplateEditError extends Error {}

const clone = (x) => JSON.parse(JSON.stringify(x));
const kids = (row) => row.children || [];

let counter = 0;
export const newTemplateId = () => `t${Date.now().toString(36)}${(counter++).toString(36)}`;

export function findRow(rows, id, parent = null) {
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].id === id) return { list: rows, index: i, row: rows[i], parent };
    const hit = findRow(kids(rows[i]), id, rows[i]);
    if (hit) return hit;
  }
  return null;
}

export function allIds(rows, out = new Set()) {
  rows.forEach((r) => {
    out.add(r.id);
    allIds(kids(r), out);
  });
  return out;
}

export function canHoldRow(parent, kind) {
  if (!parent) return kind === "discipline";
  if (parent.kind === "system") return false;
  return !kids(parent).length || kids(parent)[0].kind === kind;
}

/** Template codes (1, 2.2, 4.1.1) from the order of the rows. */
export function renumber(rows, prefix = "") {
  return rows.map((r, i) => {
    const code = prefix ? `${prefix}.${i + 1}` : String(i + 1);
    const out = { ...r, code };
    if (r.children) out.children = renumber(r.children, code);
    return out;
  });
}

export function addRow(rows, parentId, row) {
  const t = clone(rows);
  const parent = parentId ? findRow(t, parentId)?.row : null;
  if (parentId && !parent) throw new TemplateEditError("მშობელი ვერ მოიძებნა");
  if (!canHoldRow(parent, row.kind)) {
    throw new TemplateEditError(
      !parent ? "ზედა დონეზე მხოლოდ დისციპლინაა" : parent.kind === "system" ? "სისტემა სხვა ხაზს ვერ შეიცავს" : "აქ ან ქვე-დისციპლინებია, ან სისტემები — ორივე ერთად არა"
    );
  }
  if (parent) parent.children = [...kids(parent), row];
  else t.push(row);
  return renumber(t);
}

export function updateRow(rows, id, patch) {
  const t = clone(rows);
  const hit = findRow(t, id);
  if (!hit) throw new TemplateEditError("ხაზი ვერ მოიძებნა");
  if (patch.kind && patch.kind !== hit.row.kind) {
    if (patch.kind === "system" && kids(hit.row).length) throw new TemplateEditError("ხაზს შვილები აქვს — სისტემა ვერ გახდება");
    const siblings = hit.list.filter((r) => r.id !== id);
    if (!hit.parent && patch.kind === "system") throw new TemplateEditError("ზედა დონეზე მხოლოდ დისციპლინაა");
    if (siblings.length && siblings[0].kind !== patch.kind) throw new TemplateEditError("გვერდით ხაზები სხვა ტიპისაა");
  }
  Object.entries(patch).forEach(([k, v]) => {
    if (k !== "name_ka" && (v === "" || v === undefined || v === null)) delete hit.row[k];
    else hit.row[k] = v;
  });
  return t;
}

export function removeRow(rows, id) {
  const prune = (list) => list.filter((r) => r.id !== id).map((r) => (r.children ? { ...r, children: prune(r.children) } : r));
  return renumber(prune(clone(rows)));
}

export function moveRow(rows, id, dir) {
  const t = clone(rows);
  const hit = findRow(t, id);
  if (!hit) throw new TemplateEditError("ხაზი ვერ მოიძებნა");
  const to = hit.index + dir;
  if (to < 0 || to >= hit.list.length) throw new TemplateEditError("უფრო შორს ვერ გადავა");
  const [row] = hit.list.splice(hit.index, 1);
  hit.list.splice(to, 0, row);
  return renumber(t);
}

/** Preset lists keep only ids that still exist (a removed row leaves every preset). */
export function cleanPresets(presets, rows) {
  const ids = allIds(rows);
  const out = { ...presets };
  ["building_with_others", "common_object_0"].forEach((k) => {
    if (Array.isArray(out[k])) out[k] = out[k].filter((id) => ids.has(id));
  });
  return out;
}
